import React from 'react';
import {
  getRiskLevelConfig,
  formatRiskScore,
  getRiskFactorDisplay,
} from '../../../services/networkIntelligence.service';

/**
 * Card component displaying association risk score and factors.
 */
const AssociationRiskCard = ({ riskData }) => {
  if (!riskData) {
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-semibold mb-3">Association Risk</h3>
        <p className="text-gray-500 text-sm">No risk data available</p>
      </div>
    );
  }

  const riskConfig = getRiskLevelConfig(riskData.communityRiskLevel);
  const riskScore = riskData.associationRiskScore || 0;

  // Determine score category
  let scoreCategory = 'low';
  if (riskScore >= 0.75) scoreCategory = 'critical';
  else if (riskScore >= 0.5) scoreCategory = 'high';
  else if (riskScore >= 0.25) scoreCategory = 'medium';

  const scoreConfig = getRiskLevelConfig(scoreCategory);

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <h3 className="text-lg font-semibold mb-3">Association Risk</h3>

      {/* Risk Score Gauge */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm text-gray-600">Risk Score</span>
          <span
            className="text-2xl font-bold"
            style={{ color: scoreConfig.color }}
          >
            {formatRiskScore(riskScore)}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className="h-3 rounded-full transition-all duration-500"
            style={{
              width: `${Math.min(100, riskScore * 100)}%`,
              backgroundColor: scoreConfig.color,
            }}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>0%</span>
          <span>50%</span>
          <span>100%</span>
        </div>
      </div>

      {/* Connection Stats */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-gray-50 p-2 rounded">
          <div className="text-gray-500 text-xs">Total Connections</div>
          <div className="text-xl font-semibold">{riskData.totalConnections || 0}</div>
        </div>
        <div className="bg-gray-50 p-2 rounded">
          <div className="text-gray-500 text-xs">High-Risk Connections</div>
          <div
            className="text-xl font-semibold"
            style={{ color: riskData.highRiskConnections > 0 ? '#EF4444' : 'inherit' }}
          >
            {riskData.highRiskConnections || 0}
          </div>
        </div>
      </div>

      {/* Community Risk Level */}
      <div className="mb-4">
        <div className="text-sm text-gray-600 mb-1">Community Risk Level</div>
        <span
          className="inline-block px-3 py-1 rounded-full text-sm font-medium"
          style={{
            backgroundColor: riskConfig.bgColor,
            color: riskConfig.color,
          }}
        >
          {riskConfig.label}
        </span>
      </div>

      {/* Risk Factors */}
      <div>
        <div className="text-sm text-gray-600 mb-2">Risk Factors</div>
        {riskData.riskFactors && riskData.riskFactors.length > 0 ? (
          <ul className="space-y-1">
            {riskData.riskFactors.map((factor, idx) => (
              <li
                key={idx}
                className="flex items-center text-sm"
              >
                <span
                  className={`w-2 h-2 rounded-full mr-2 ${
                    factor.includes('high_risk') ? 'bg-red-500' :
                    factor.includes('no_risk') ? 'bg-green-500' : 'bg-yellow-500'
                  }`}
                />
                {getRiskFactorDisplay(factor)}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-400 text-sm">No risk factors identified</p>
        )}
      </div>
    </div>
  );
};

export default AssociationRiskCard;
